// Verified facts page — a plain, citation-friendly surface for journalists,
// researchers, and answer engines. Every statement is repository-sourced from
// the approved press copy; nothing here is generated or inferred.
import { createFileRoute, Link } from "@tanstack/react-router";
import { SITE_URL } from "@/lib/site";
import { DATA_FEEDS } from "@/lib/data-feed";
import { ENTITY_IDS, entityLastModified } from "@/lib/entity";
import {
  BOILERPLATE,
  CANONICAL_IDENTITY_LINE,
  FACT_SHEET,
  OFFICIAL_PROFILES,
  PRESS_BIO_100,
  PRESS_BIO_30,
  PRESS_EMAIL,
  PRESS_RESOURCES,
} from "@/lib/press";
import {
  breadcrumbs,
  ldScript,
  orgRef,
  pageNode,
  personRef,
  seoMeta,
} from "@/lib/schema";

const PATH = "/press/factsheet";
const PAGE_URL = `${SITE_URL}${PATH}`;
const TITLE = "Buddha KA Fact Sheet | Verified Facts, Bios & Official Links";
const DESCRIPTION =
  "Verified facts about Buddha KA and KAfrequency: names, origin, roles, framework, official profiles, press contact, and machine-readable sources.";

export const Route = createFileRoute("/press/factsheet")({
  head: () => ({
    ...seoMeta({ title: TITLE, description: DESCRIPTION, path: PATH }),
    scripts: [
      ldScript(
        pageNode({
          type: "WebPage",
          id: `${PAGE_URL}#factsheet`,
          url: PAGE_URL,
          name: "Buddha KA Fact Sheet",
          description: DESCRIPTION,
          about: [personRef(), orgRef()],
        }),
      ),
      ldScript(
        breadcrumbs([
          ["Press", "/press"],
          ["Fact sheet", PATH],
        ]),
      ),
    ],
  }),
  component: FactSheetPage,
});

function FactSheetPage() {
  return (
    <main className="mx-auto w-full max-w-3xl px-5 py-16 md:py-24">
      <nav aria-label="Breadcrumb" className="mb-6 text-xs uppercase tracking-[0.2em] text-muted-foreground">
        <Link to="/press" className="hover:text-primary">
          Press
        </Link>
        <span aria-hidden="true"> / </span>
        <span>Fact sheet</span>
      </nav>

      <h1 className="font-display text-3xl md:text-5xl text-foreground">Buddha KA — verified fact sheet</h1>
      <p className="mt-4 text-muted-foreground">
        First-party, verified information for journalists, researchers, and machine readers. Facts below are
        maintained on buddhaka.com and are the canonical source for this artist and this framework.
      </p>

      <p className="mt-6 rounded-2xl border border-border/60 bg-muted/20 p-5 font-serif text-lg italic text-foreground">
        {CANONICAL_IDENTITY_LINE}
      </p>

      <section aria-labelledby="facts" className="mt-12">
        <h2 id="facts" className="font-display text-xl text-primary">
          Core facts
        </h2>
        <dl className="mt-4 divide-y divide-border/60 border-y border-border/60">
          {FACT_SHEET.map(([label, value]) => (
            <div key={label} className="grid gap-1 py-3 md:grid-cols-[220px_1fr] md:gap-6">
              <dt className="text-sm uppercase tracking-wide text-muted-foreground">{label}</dt>
              <dd className="text-foreground">{value}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section aria-labelledby="bios" className="mt-12">
        <h2 id="bios" className="font-display text-xl text-primary">
          Approved biographies
        </h2>
        <h3 className="mt-4 text-sm uppercase tracking-wide text-muted-foreground">Short (~30 words)</h3>
        <p className="mt-1 text-foreground">{PRESS_BIO_30}</p>
        <h3 className="mt-5 text-sm uppercase tracking-wide text-muted-foreground">Standard (~100 words)</h3>
        <p className="mt-1 text-foreground">{PRESS_BIO_100}</p>
        <h3 className="mt-5 text-sm uppercase tracking-wide text-muted-foreground">Boilerplate</h3>
        <p className="mt-1 text-foreground">{BOILERPLATE}</p>
        <p className="mt-4 text-sm text-muted-foreground">
          Long-form biography, photography, and usage terms live in the{" "}
          <Link to="/press" className="text-primary hover:underline">
            full press kit
          </Link>
          .
        </p>
      </section>

      <section aria-labelledby="profiles" className="mt-12">
        <h2 id="profiles" className="font-display text-xl text-primary">
          Official profiles
        </h2>
        <ul className="mt-4 grid gap-2 sm:grid-cols-2">
          {OFFICIAL_PROFILES.map((p) => (
            <li key={p.url}>
              <a
                href={p.url}
                rel="me noopener"
                target="_blank"
                className="text-foreground underline-offset-4 hover:text-primary hover:underline"
              >
                {p.platform}
              </a>
            </li>
          ))}
        </ul>
        <p className="mt-4 text-sm text-muted-foreground">
          Any profile not listed here is not an official Buddha KA account.
        </p>
      </section>

      <section aria-labelledby="governance" className="mt-12">
        <h2 id="governance" className="font-display text-xl text-primary">
          Provenance and canonical identifiers
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          These facts are held in one governed registry on buddhaka.com and read by every page, feed, and
          structured-data node here. Each fact carries a verification state and the date it last actually
          changed; the full record — including provenance notes — is published at{" "}
          <a href="/data/entity-registry.json" className="text-primary hover:underline">
            /data/entity-registry.json
          </a>
          .
        </p>
        <dl className="mt-4 divide-y divide-border/60 border-y border-border/60">
          <div className="grid gap-1 py-3 md:grid-cols-[220px_1fr] md:gap-6">
            <dt className="text-sm uppercase tracking-wide text-muted-foreground">Person identifier</dt>
            <dd className="break-all font-mono text-sm text-foreground">{ENTITY_IDS.person}</dd>
          </div>
          <div className="grid gap-1 py-3 md:grid-cols-[220px_1fr] md:gap-6">
            <dt className="text-sm uppercase tracking-wide text-muted-foreground">KAfrequency identifier</dt>
            <dd className="break-all font-mono text-sm text-foreground">{ENTITY_IDS.organization}</dd>
          </div>
          <div className="grid gap-1 py-3 md:grid-cols-[220px_1fr] md:gap-6">
            <dt className="text-sm uppercase tracking-wide text-muted-foreground">Facts last changed</dt>
            <dd className="text-foreground">
              <time dateTime={entityLastModified()}>{entityLastModified()}</time>
            </dd>
          </div>
        </dl>
      </section>

      <section aria-labelledby="machine" className="mt-12">
        <h2 id="machine" className="font-display text-xl text-primary">
          Machine-readable sources
        </h2>
        <ul className="mt-4 space-y-2">
          {[...DATA_FEEDS.map((f) => ({ label: f.label, path: f.path, description: f.description })), ...PRESS_RESOURCES].map(
            (r) => (
              <li key={r.path}>
                <a href={r.path} className="text-foreground underline-offset-4 hover:text-primary hover:underline">
                  {r.label}
                </a>
                <span className="text-muted-foreground"> — {r.description}</span>
              </li>
            ),
          )}
        </ul>
      </section>

      <section aria-labelledby="contact" className="mt-12">
        <h2 id="contact" className="font-display text-xl text-primary">
          Press contact
        </h2>
        <p className="mt-2 text-foreground">
          <a href={`mailto:${PRESS_EMAIL}`} className="hover:text-primary">
            {PRESS_EMAIL}
          </a>{" "}
          ·{" "}
          <Link to="/contact" className="text-primary hover:underline">
            Contact form
          </Link>
        </p>
        <p className="mt-6 text-sm text-muted-foreground">
          Canonical entity pages:{" "}
          <Link to="/artist/buddha-ka" className="text-primary hover:underline">
            Buddha KA
          </Link>{" "}
          ·{" "}
          <Link to="/about" className="text-primary hover:underline">
            About
          </Link>{" "}
          ·{" "}
          <Link to="/codex" className="text-primary hover:underline">
            Concepts
          </Link>
        </p>
      </section>
    </main>
  );
}
